/**
 * Documents module — per-child file metadata list. Uses the two-step upload:
 * POST returns a DocumentUploadTicket (the mock fakes the SAS URL). In the
 * localStorage build, only metadata is stored; against the real API the client
 * would PUT bytes to ticket.uploadUrl next.
 */
import { useState } from 'react';
import type { Document as SolandiaDocument } from '@solandia/core';
import { createDocumentInputSchema } from '@solandia/core';
import { useI18n } from '../../i18n';
import { useApi } from '../../api/ApiProvider';
import { useFamily } from '../../family/FamilyContext';
import { useAsync } from '../../hooks/useAsync';
import {
  Badge,
  Dialog,
  EmptyState,
  ErrorState,
  Field,
  Form,
  SectionHeader,
  Spinner,
} from '../../components/ui';

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function DocumentsModule() {
  const { t } = useI18n();
  const api = useApi();
  const { family, activeChild } = useFamily();
  const childId = activeChild!.id;

  const { data, loading, error, reload } = useAsync<SolandiaDocument[]>(async () => {
    const res = await api.listDocuments(family.id, childId);
    return res.items;
  }, [family.id, childId]);

  const [adding, setAdding] = useState(false);

  async function remove(id: string) {
    if (!window.confirm(t.common.confirmDelete)) return;
    await api.deleteDocument(family.id, childId, id);
    reload();
  }

  return (
    <section>
      <SectionHeader
        title={t.documents.title}
        subtitle={<Badge tone="accent">{t.documents.sensitive}</Badge>}
        action={
          <button type="button" className="btn btn-primary btn-sm" onClick={() => setAdding(true)}>
            ＋ {t.documents.add}
          </button>
        }
      />

      {loading ? <Spinner /> : null}
      {error ? <ErrorState message={t.errors.generic} onRetry={reload} /> : null}
      {data && data.length === 0 ? (
        <EmptyState emoji="📄" title={t.documents.empty} />
      ) : null}

      {data && data.length > 0 ? (
        <ul className="list">
          {data.map((d) => (
            <li key={d.id} className="list-item">
              <div className="grow stack-sm">
                <div className="row wrap">
                  <strong>{d.name}</strong>
                  <Badge>{d.category}</Badge>
                </div>
                <span className="text-sm muted">
                  {formatSize(d.sizeBytes)} · {new Date(d.createdAt).toLocaleDateString('sk-SK')}
                </span>
                {d.note ? <span className="text-sm">{d.note}</span> : null}
              </div>
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => remove(d.id)}
                aria-label={`${t.common.delete} ${d.name}`}
              >
                {t.common.delete}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {adding ? (
        <AddDocumentDialog
          onClose={() => setAdding(false)}
          onCreated={() => {
            setAdding(false);
            reload();
          }}
        />
      ) : null}
    </section>
  );
}

function AddDocumentDialog({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const { t } = useI18n();
  const api = useApi();
  const { family, activeChild } = useFamily();
  const [name, setName] = useState('');
  const [category, setCategory] = useState('Doklady');
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  async function submit() {
    const input = {
      name: (name.trim() || file?.name || '').trim(),
      category: category.trim(),
      mimeType: file?.type || 'application/octet-stream',
      sizeBytes: file?.size ?? 0,
      note: null,
    };
    const parsed = createDocumentInputSchema.safeParse(input);
    if (!parsed.success) {
      setErrors({ name: t.common.required });
      return;
    }
    setBusy(true);
    try {
      // POST → ticket. In the real backend the next step PUTs bytes to
      // ticket.uploadUrl; the mock just records metadata.
      await api.createDocumentUpload(family.id, activeChild!.id, parsed.data);
      onCreated();
    } catch {
      setErrors({ name: t.errors.generic });
      setBusy(false);
    }
  }

  return (
    <Dialog title={t.documents.add} onClose={onClose} closeLabel={t.common.close}>
      <Form onSubmit={submit}>
        <Field label={t.documents.fileLabel} hint={t.documents.fileHint}>
          {({ id }) => (
            <input
              id={id}
              type="file"
              className="input"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                setFile(f);
                if (f && !name.trim()) setName(f.name);
              }}
            />
          )}
        </Field>
        <Field label={t.documents.nameLabel} error={errors.name}>
          {({ id, invalid }) => (
            <input
              id={id}
              className="input"
              value={name}
              aria-invalid={invalid}
              onChange={(e) => setName(e.target.value)}
            />
          )}
        </Field>
        <Field label={t.documents.categoryLabel}>
          {({ id }) => (
            <input
              id={id}
              className="input"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            />
          )}
        </Field>
        <div className="row" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t.common.cancel}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? t.common.saving : t.common.add}
          </button>
        </div>
      </Form>
    </Dialog>
  );
}
